/** @jsx jsx */
import { jsx } from '@emotion/react';
import { Redirect, useLocation } from 'react-router';

import { InsightsExpandButton } from 'src/app/graph/insights/InsightsExpandButton';
import { Loading } from 'src/components/common/loading/Loading';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { InsightsEnforcementWarning } from 'src/components/insightsEnforcementWarning/InsightsEnforcementWarning';
import { useRouteParams } from 'src/hooks/useRouteParams';
import {
  TimeRangeErrorCode,
  resetTimeRangeLocation,
  useSelectedTimeRange,
} from 'src/hooks/useSelectedTimeRange';
import Config from 'src/lib/config';
import { TimeRangeOption } from 'src/lib/config/timeRangeOptions';
import { useShowSubgraphInsightsUI } from 'src/lib/featureFlags';

import { ServiceRangeUnavailable } from '../../errors/serviceRangeUnavailable/ServiceRangeUnavailable';
import { AllowedTabsProvider } from '../../operationsPage/allowedTabsProvider/AllowedTabsProvider';
import { SelectedSubgraph } from '../../operationsPage/insightsL3Nav/insightsList/SubgraphsList';
import { useInsightsL3NavState } from '../../operationsPage/insightsL3Nav/useInsightsL3NavState';
import { InsightsFeedbackForm } from '../../operationsPage/report-pane/section-report/errors-section/federatedErrors/InsightsFeedbackForm';
import { SetupOperationMetrics } from '../../operationsPage/SetupOperationMetrics';
import { insightsRouteConfig } from '../../routes';

import { SubgraphPageCharts } from './charts/SubgraphPageCharts';
import {
  SubgraphInsightsSetupConfigCTA,
  SubgraphInsightsUpgradeRouterCTA,
} from './SubgraphInsightsCTA';
import {
  SubgraphInsightsFilter,
  buildSubgraphInsightsFilter,
} from './SubgraphInsightsFilter';
import { TopTrafficSourcesSection } from './topTrafficSources/TopTrafficSourcesSection';

interface SubgraphMainContentProps {
  serviceId: string | undefined;
  accountId: string | undefined;
  reportingEnabled?: boolean;
  showUpgradeRouterCTA?: boolean;
  showSetupConfigCTA?: boolean;
  selectedSubgraph?: SelectedSubgraph;
}

export const SubgraphInsightsMainContent = ({
  serviceId,
  accountId,
  reportingEnabled,
  showUpgradeRouterCTA,
  showSetupConfigCTA,
  selectedSubgraph,
}: SubgraphMainContentProps) => {
  const location = useLocation();
  const { isOpen, onOpen } = useInsightsL3NavState();

  const params = useRouteParams(insightsRouteConfig);
  const {
    graphId,
    graphVariant = Config.service.defaultVariant,
    insightsTab,
    fastModeRange,
  } = params;
  const showSubgraphInsights = useShowSubgraphInsightsUI();
  const {
    timeRangeInfo,
    loading: timeRangeLoading,
    error: timeRangeError,
  } = useSelectedTimeRange();
  if (!serviceId) {
    return null;
  }

  if (insightsTab !== 'subgraphs' || !showSubgraphInsights) {
    return (
      <Redirect
        to={insightsRouteConfig.locationFrom({
          location,
          patch: { insightsTab: 'operations' },
        })}
      />
    );
  }

  if (timeRangeError?.code === TimeRangeErrorCode.INVALID) {
    return <Redirect to={resetTimeRangeLocation(location)} />;
  }

  const title = selectedSubgraph?.name ?? 'Subgraphs';

  return (
    <AllowedTabsProvider isDemoPage={!accountId}>
      {() => {
        return (
          <div className="flex flex-1 flex-col bg-primary pb-8">
            <InsightsEnforcementWarning />
            <div className="sticky top-0 z-20 border-b border-primary bg-primary px-8 pt-4">
              <PageTitle
                className="border-b-0 px-[0px]"
                title={title}
                leftAdornment={
                  !isOpen ? (
                    <Tooltip label="Expand">
                      <InsightsExpandButton onOpen={onOpen} />
                    </Tooltip>
                  ) : null
                }
                rightSide={
                  <div className="flex items-center">
                    {reportingEnabled && timeRangeInfo && (
                      <SubgraphInsightsFilter
                        ranges={
                          timeRangeInfo.accountCapabilities
                            .ranges as ReadonlyArray<TimeRangeOption>
                        }
                        maxRangeInDays={
                          timeRangeInfo.accountCapabilities.maxRangeInDays
                        }
                        fastModeRange={fastModeRange}
                      />
                    )}
                  </div>
                }
              />
            </div>
            <div className="relative size-full px-8 pt-6">
              <div className="flex justify-center">
                {!reportingEnabled ? (
                  <SetupOperationMetrics />
                ) : showUpgradeRouterCTA ? (
                  <SubgraphInsightsUpgradeRouterCTA />
                ) : showSetupConfigCTA ? (
                  <SubgraphInsightsSetupConfigCTA />
                ) : timeRangeLoading ? (
                  <Loading />
                ) : !timeRangeInfo ? (
                  <ServiceRangeUnavailable />
                ) : (
                  <div className="flex-1">
                    <div className="mb-4 @container">
                      <TopTrafficSourcesSection
                        graphId={graphId}
                        filter={buildSubgraphInsightsFilter(params)}
                      />
                    </div>
                    <SubgraphPageCharts
                      graphId={graphId}
                      graphVariant={graphVariant}
                      selectedSubgraph={selectedSubgraph as SelectedSubgraph}
                      selectedRange={timeRangeInfo.selectedRange}
                    />
                  </div>
                )}
              </div>
              <div className="mt-8">
                <InsightsFeedbackForm
                  form="https://docs.google.com/forms/d/e/1FAIpQLSebR5hZWmWn4zZs2vMiEFC4IsIzlUvKM4zqnIIPDI0T4-s8eA/formResponse"
                  featureName="subgraph insights"
                  featureDescription="We've added subgraph-level insights"
                  hrefId="1275268882"
                  ratingId="32594182"
                  graphRefId="942070176"
                  textFeedbackId="785422751"
                  localStorageKey="hasCompletedSubgraphInsightsFeedback"
                />
              </div>
            </div>
          </div>
        );
      }}
    </AllowedTabsProvider>
  );
};
